import React, { useEffect, useRef, useState } from "react";
import { FaCheckCircle } from "react-icons/fa";

function Australiainvestorvisaimport() {
  const [activeIndex, setActiveIndex] = useState(0);
  const sectionRefs = useRef([]);
  const contentRef = useRef(null);
  const titleRef = useRef(null);

  useEffect(() => {
    sectionRefs.current = sectionRefs.current.slice(0, 5);

    const observerOptions = {
      root: null,
      rootMargin: "0px",
      threshold: 0.1,
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const index = sectionRefs.current.indexOf(entry.target);
          setActiveIndex(index);
        }
      });
    }, observerOptions);

    sectionRefs.current.forEach((section) => {
      observer.observe(section);
    });

    return () => {
      observer.disconnect();
    };
  }, []);

  const scrollToSection = (index) => {
    setActiveIndex(index);
    const section = sectionRefs.current[index];
    const contentElement = contentRef.current;

    const scrollPosition = section.offsetTop - contentElement.offsetTop;

    contentElement.scrollTo({
      top: scrollPosition,
      behavior: "smooth",
    });
  };

  return (
    <div className="flex flex-wrap">
      <div className=" mt-20 flex min-h-screen">
        <div
          ref={titleRef}
          className="sticky top-20 hidden h-screen w-1/4 overflow-y-auto bg-gray-100 p-4 md:block"
        >
          <ul className="space-y-2">
            <li
              onClick={() => scrollToSection(0)}
              className={`cursor-pointer p-8 ${
                activeIndex === 0
                  ? "bg-blue-500 text-white"
                  : "bg-white text-[#2C3F69]"
              } shadow rounded-md`}
            >
              What is the Australia Investor Visa?
            </li>
            <li
              onClick={() => scrollToSection(1)}
              className={`cursor-pointer p-8 ${
                activeIndex === 1
                  ? "bg-blue-500 text-white"
                  : "bg-white text-[#2C3F69]"
              } shadow rounded-md`}
            >
              Eligibility Requirements for the Australia Investor Visa
            </li>
            <li
              onClick={() => scrollToSection(2)}
              className={`cursor-pointer p-8 ${
                activeIndex === 2
                  ? "bg-blue-500 text-white"
                  : "bg-white text-[#2C3F69]"
              } shadow rounded-md`}
            >
              Documentation Required for the Australia Investor Visa
            </li>
            <li
              onClick={() => scrollToSection(3)}
              className={`cursor-pointer p-8 ${
                activeIndex === 3
                  ? "bg-blue-500 text-white"
                  : "bg-white text-[#2C3F69]"
              } shadow rounded-md`}
            >
              How to Apply for the Australia Investor Visa
            </li>
          </ul>
        </div>

        <div
          ref={contentRef}
          className="mb-10 h-screen overflow-y-auto p-4 md:w-3/4 lg:w-3/4 xl:w-3/4"
        >
          <div ref={(el) => (sectionRefs.current[0] = el)}>
            <p className="text-xl font-bold text-[#011473]">
              What is the Australia Investor Visa?
            </p>
            <p className="mt-4 text-md text-[#2C3F69]">
              The Australia Investor Visa is a unique immigration pathway designed for high-net-worth individuals who wish to invest in Australia's economy and obtain permanent residency. This visa is specifically intended for business owners and investors who are capable of making a substantial contribution to the Australian economy. The primary visa categories under this program are:
            </p>
            <ul className="mt-4 list-disc pl-6 text-[#2C3F69]">
              <li>Subclass 188: Business Innovation and Investment (Provisional) Visa</li>
              <li>Subclass 888: Business Innovation and Investment (Permanent) Visa</li>
            </ul>
          </div>

          <div ref={(el) => (sectionRefs.current[1] = el)} className="mt-10">
            <p className="text-xl font-bold text-[#011473]">
              Eligibility Requirements for the Australia Investor Visa
            </p>
            <p className="mt-4 text-md text-[#2C3F69]">
              To be eligible for the Australia Investor Visa, you need to meet the following criteria:
            </p>
            <ul className="mt-4 list-disc pl-6 text-[#2C3F69]">
              <li>Age: Applicants are typically required to be under 55 years old, though there may be exceptions depending on your circumstances.</li>
              <li>Investment Funds: You must demonstrate that you possess the necessary funds to make a qualifying investment in Australia.</li>
              <li>Business Experience: You need to show a successful history in business or investment management.</li>
              <li>Net Worth: You must have legally accumulated net assets valued at no less than AUD 2.5 million.</li>
              <li>Health and Character Standards: All applicants must meet the health and character requirements established by the Australian government.</li>
            </ul>
          </div>

          <div ref={(el) => (sectionRefs.current[2] = el)} className="mt-10">
            <p className="text-xl font-bold text-[#011473]">
              Documentation Required for the Australia Investor Visa
            </p>
            <ul className="mt-4 list-disc pl-6 text-[#2C3F69]">
              <li>Valid Passport: Your passport should remain valid for at least six months beyond your intended date of arrival in Australia.</li>
              <li>Expression of Interest (EOI): Submit an Expression of Interest (EOI) via the SkillSelect platform.</li>
              <li>Investment Proposal: You must provide a comprehensive investment plan detailing your proposed investment activities within Australia.</li>
              <li>Proof of Business Experience: You need to provide supporting documents verifying your business experience, such as:
                <ul className="mt-2 list-inside list-disc pl-6 text-[#2C3F69]">
                  <li>Business registration certificates</li>
                  <li>Financial statements</li>
                  <li>Taxation records</li>
                </ul>
              </li>
              <li>Proof of Net Assets: Evidence must be provided showing that you have legally acquired assets worth at least AUD 2.5 million. Documentation can include:
                <ul className="mt-2 list-inside list-disc pl-6 text-[#2C3F69]">
                  <li>Bank statements</li>
                  <li>Property appraisals</li>
                  <li>Stock or bond certificates</li>
                </ul>
              </li>
              <li>Investment Fund Evidence: You must show that the funds you intend to invest are readily available. This can be supported by:
                <ul className="mt-2 list-inside list-disc pl-6 text-[#2C3F69]">
                  <li>Proof of funds transfer</li>
                  <li>Bank statements reflecting the available funds</li>
                </ul>
              </li>
              <li>Health and Character Requirements:
                <ul className="mt-2 list-inside list-disc pl-6 text-[#2C3F69]">
                  <li>Health Check: A health examination by an approved panel physician is required.</li>
                  <li>Police Clearance: Police certificates from every country where you’ve lived for 12 months or more in the last 10 years.</li>
                </ul>
              </li>
              <li>English Language Proficiency: You will need to provide proof of your English proficiency through recognized tests like IELTS, TOEFL, or PTE Academic.</li>
              <li>Identity Verification: Submit official identity documents, such as birth certificates or national IDs and any legal name change records, if applicable.</li>
              <li>Family Documents: If you are applying with family members, you will need to provide documentation supporting your relationship, such as marriage certificates, birth certificates, or joint financial records.</li>
            </ul>
          </div>

          <div ref={(el) => (sectionRefs.current[3] = el)} className="mt-10">
            <p className="text-xl font-bold text-[#011473]">
              How to Apply for the Australia Investor Visa
            </p>
            <ul className="mt-4 list-disc pl-6 text-[#2C3F69]">
              <li>Verify Your Eligibility: Confirm that you meet all the required criteria for the visa.</li>
              <li>Submit Your Expression of Interest (EOI): Complete and submit your EOI through the SkillSelect platform.</li>
              <li>Wait for an Invitation: If your EOI is selected, you will receive an invitation to apply for the visa.</li>
              <li>Prepare Your Documentation: Gather all necessary documents as per the requirements listed above.</li>
              <li>Submit Your Application: Once you have all the required documents, submit your application online.</li>
              <li>Wait for the Decision: After submission, you will need to wait for a response from the Department of Home Affairs regarding your application status.</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Australiainvestorvisaimport;
